import { useEffect, useRef, useState } from 'react'

interface UseTrumbowygInitOptions {
  onPaste?: (html: string) => string | void
  onChange?: (html: string) => void
}

/**
 * Initialize and manage Trumbowyg editor lifecycle
 * Handles jQuery dynamic import, editor initialization, event binding, and cleanup
 */
export const useTrumbowygInit = (
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  options: UseTrumbowygInitOptions = {}
) => {
  const [isReady, setIsReady] = useState(false)
  // Set true while useEditorSync programmatically sets editor content, so tbwchange doesn't echo it back as an onChange
  const isUpdatingRef = useRef(false)
  // Signals that the editor already has the latest content — useEditorSync should skip the sync to preserve cursor position
  const isInternalChangeRef = useRef(false)
  const onChangeRef = useRef(options.onChange)
  const onPasteRef = useRef(options.onPaste)

  // Keep callback refs up to date
  useEffect(() => {
    onChangeRef.current = options.onChange
  }, [options.onChange])

  useEffect(() => {
    onPasteRef.current = options.onPaste
  }, [options.onPaste])

  useEffect(() => {
    let mounted = true
    const textarea = textareaRef.current
    const previousJQuery = window.jQuery
    const previous$ = window.$

    // Declared here so the cleanup return can reach them after the async init.
    let contentEditable: HTMLElement | null = null
    // True only after the editor is fully initialized and setIsReady(true) has been called.
    // Trumbowyg fires a synthetic empty tbwchange during init — the isInitialized guard
    // prevents that from leaking out as an onChange('') call.
    let isInitialized = false
    let metaKeyHeld = false

    // Trumbowyg's keyup handler sets ctrl=true on keydown for any meta/ctrl keystroke,
    // then only fires tbwchange for Ctrl+Z/Y (undo/redo). Every other meta+delete
    // operation (cmd+backspace → deleteHardLineBackward, ctrl+backspace, etc.) silently
    // drops the change because the ctrl flag blocks the normal tbwchange branch.
    //
    // We supplement tbwchange by listening to the native input event on the contentEditable:
    // - Track whether a meta/ctrl key was held on the preceding keydown.
    // - On input, fire onChange for undo/redo (historyUndo/historyRedo) and for
    //   delete-type inputTypes caused by a meta/ctrl key that tbwchange misses.
    // - Read from contentEditable.innerHTML directly — the browser has already applied
    //   the change by the time the input event fires.
    // - Exclude 'deleteByCut' (cmd+x): Trumbowyg handles that via its 'cut' event handler.
    //
    // Undo/redo note: Trumbowyg's tbwchange for Z/Y only fires when e.metaKey is still
    // true on keyup, which is unreliable if the user releases Meta before Z. Listening
    // to historyUndo/historyRedo on the input event is the canonical, key-order-independent
    // way to detect undo/redo in a contentEditable.
    const onEditorKeyDown = (e: KeyboardEvent): void => {
      metaKeyHeld = (e.metaKey || e.ctrlKey) && !e.altKey
    }

    const onEditorInput = (e: Event): void => {
      if (isUpdatingRef.current || !onChangeRef.current || !contentEditable) return
      const inputType = (e as InputEvent).inputType ?? ''

      // Undo/redo: the DOM is already restored when the input event fires, so read
      // innerHTML directly. No metaKeyHeld check — undo can be triggered any way.
      if (inputType === 'historyUndo' || inputType === 'historyRedo') {
        isInternalChangeRef.current = true
        onChangeRef.current(contentEditable.innerHTML)
        return
      }

      // meta+delete operations (cmd+backspace, ctrl+backspace, etc.)
      if (!metaKeyHeld || !inputType.startsWith('delete') || inputType === 'deleteByCut') return
      isInternalChangeRef.current = true
      onChangeRef.current(contentEditable.innerHTML)
    }

    const initializeEditor = async () => {
      try {
        // Dynamically import jQuery and expose on window for Trumbowyg
        const jQueryPkg = await import('jquery')
        const $ = jQueryPkg.default
        window.jQuery = $
        window.$ = $

        // Import and register Trumbowyg
        // @ts-expect-error - trumbowyg has no type definitions
        await import('trumbowyg')

        if (!textarea || !mounted) return

        const $editor = $(textarea)

        // Initialize Trumbowyg
        $editor.trumbowyg({
          btns: [], // No toolbar
          autogrow: false, // Manual height control via CSS
          removeformatPasted: false, // Preserve Word/Excel formatting
          semantic: false, // Use <br> instead of <p> for line breaks
          resetCss: false, // Don't reset CSS - preserve original formatting
        })

        // Wire up change events
        $editor.on('tbwchange.trumbowygEditor', () => {
          if (isInitialized && !isUpdatingRef.current && onChangeRef.current) {
            isInternalChangeRef.current = true
            onChangeRef.current($editor.trumbowyg('html'))
          }
        })

        // Wire up paste events
        $editor.on('tbwpaste.trumbowygEditor', () => {
          setTimeout(() => {
            const html = $editor.trumbowyg('html')
            if (onPasteRef.current) {
              const rawHtml = onPasteRef.current(html)
              if (rawHtml !== undefined && rawHtml !== html) {
                isUpdatingRef.current = true
                $editor.trumbowyg('html', rawHtml)
                isUpdatingRef.current = false
                if (onChangeRef.current) {
                  onChangeRef.current(rawHtml)
                }
              }
            }
          }, 0)
        })

        // After init the textarea has been moved inside t.$box. The contentEditable
        // (.trumbowyg-editor) is a sibling inside the same box — attach supplemental listeners.
        contentEditable = textarea.parentElement?.querySelector('.trumbowyg-editor') ?? null
        if (contentEditable) {
          contentEditable.addEventListener('keydown', onEditorKeyDown)
          contentEditable.addEventListener('input', onEditorInput)
        }

        if (!mounted) return
        setIsReady(true)
        isInitialized = true
      } catch (error) {
        console.error('Failed to initialize Trumbowyg:', error)
      }
    }

    initializeEditor()

    return () => {
      mounted = false
      if (contentEditable) {
        contentEditable.removeEventListener('keydown', onEditorKeyDown)
        contentEditable.removeEventListener('input', onEditorInput)
      }
      if (textarea && window.$) {
        try {
          const $editor = window.$(textarea)
          $editor.off('tbwchange.trumbowygEditor')
          $editor.off('tbwpaste.trumbowygEditor')
          $editor.trumbowyg('destroy')
        } catch {
          // Ignore if already destroyed
        }
      }

      window.jQuery = previousJQuery
      window.$ = previous$
    }
  }, [textareaRef])

  return { isReady, isUpdatingRef, isInternalChangeRef }
}
